// src/components/games/pipsWordPuzzle/gameLogic.ts

import { LetterGuess, LetterStatus, GuessResult } from './types';
import { isWordValid } from './dictionaryService';
import { isWordInDictionary } from './wordlist';

export function checkGuess(guess: string, targetWord: string): GuessResult {
  const guessUpper = guess.toUpperCase();
  const targetUpper = targetWord.toUpperCase();
  const letters: LetterGuess[] = [];

  // Count remaining letters in target (for yellow highlighting)
  const targetLetterCounts: { [key: string]: number } = {};
  for (const letter of targetUpper) {
    targetLetterCounts[letter] = (targetLetterCounts[letter] || 0) + 1;
  }

  // First pass: mark correct letters (green)
  for (let i = 0; i < guessUpper.length; i++) {
    const letter = guessUpper[i];
    if (letter === targetUpper[i]) {
      letters.push({ letter, status: 'correct' });
      targetLetterCounts[letter]--;
    } else {
      letters.push({ letter, status: 'absent' }); // Temporary, will update in second pass
    }
  }

  // Second pass: mark present letters (yellow)
  for (let i = 0; i < letters.length; i++) {
    if (letters[i].status === 'absent') {
      const letter = letters[i].letter;
      if (targetLetterCounts[letter] && targetLetterCounts[letter] > 0) {
        letters[i].status = 'present';
        targetLetterCounts[letter]--;
      }
    }
  }

  return {
    word: guess,
    letters
  };
}

/**
 * Check if a word is valid (synchronous version using local dictionary)
 * Use this for immediate validation without async
 */
export function isValidWord(word: string): boolean {
  // Must be 3-10 letters and only contain letters
  if (!/^[A-Za-z]{3,10}$/.test(word)) {
    return false;
  }

  // Use local dictionary for synchronous validation
  return isWordInDictionary(word);
}

/**
 * Check if a word is valid (async version using Supabase)
 * Use this for the actual game validation
 */
export async function isValidWordAsync(word: string): Promise<boolean> {
  // Must be 3-10 letters and only contain letters
  if (!/^[A-Za-z]{3,10}$/.test(word)) {
    return false;
  }

  // Use Supabase dictionary with fallback to local
  return await isWordValid(word);
}

export function calculatePoints(won: boolean, guessCount: number, hintsUsed: number): number {
  if (!won) return 0;

  let points = 10; // Base points for winning

  // Bonus points for fewer guesses
  if (guessCount === 1) points += 10;
  else if (guessCount === 2) points += 8;
  else if (guessCount === 3) points += 6;
  else if (guessCount === 4) points += 4;
  else if (guessCount === 5) points += 2;

  return points;
}

export function getKeyboardStatus(guesses: GuessResult[]): Map<string, LetterStatus> {
  const keyboardMap = new Map<string, LetterStatus>();

  for (const guess of guesses) {
    for (const letterGuess of guess.letters) {
      const currentStatus = keyboardMap.get(letterGuess.letter);

      // Priority: correct > present > absent
      if (letterGuess.status === 'correct') {
        keyboardMap.set(letterGuess.letter, 'correct');
      } else if (letterGuess.status === 'present' && currentStatus !== 'correct') {
        keyboardMap.set(letterGuess.letter, 'present');
      } else if (letterGuess.status === 'absent' && !currentStatus) {
        keyboardMap.set(letterGuess.letter, 'absent');
      }
    }
  }

  return keyboardMap;
}

export const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACK']
];